import React, { useMemo, useRef, useState } from 'react'
import {
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable as DroppableOriginal,
  DroppableStateSnapshot
} from 'react-beautiful-dnd'
import patchDraggableStyle from './patchDraggableStyle'
import styled, { css } from 'styled-components'
import { useDragDropHandler } from './DragDropContext'
import DroppableWrapOriginal from './DroppableWrap'
import { createId } from '../../utils/createId'

export type DroppableProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  /**
   * So soll ein Item gerendert werden. Im Gegensatz zu "react-beautiful-dnd" soll nur
   * provided.dragHandleProps an die Kinder übergeben werden.
   *
   * provided.draggableProps und provided.innerRef werden nur der Vollständigkeit halber übergeben
   */
  children: (
    item: string,
    i: number,
    items: string[],
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ) => JSX.Element
  items: string[]
  acceptsItem?: (args: {
    draggableId: string
    item: string
    droppableSourceId: string
    droppableTargetId: string
  }) => boolean
  disabled?: boolean
  updateItems: (item: string, from: number | undefined, to: number | undefined) => unknown
  type: string
  highlightInsetLeft?: number
  highlightInsetRight?: number
  inverted?: boolean
}

const ItemWrap = styled.div<{ blockTransform?: boolean }>`
  position: relative;
  ${({ blockTransform }) =>
    blockTransform
      ? css`
          transform: translate(0, 0) !important;
        `
      : ''}
`

const emptyMargin = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
}

const emptyBox = {
  ...emptyMargin,
  width: 0,
  height: 0,
  x: 0,
  y: 0,
  center: {
    x: 0,
    y: 0
  }
}

const emptyOn = {
  client: {
    marginBox: emptyBox,
    borderBox: emptyBox,
    paddingBox: emptyBox,
    contentBox: emptyBox,
    margin: emptyMargin,
    border: emptyMargin,
    padding: emptyMargin
  },
  tagName: 'div',
  display: 'block'
}

const DroppableWrap = styled(DroppableWrapOriginal)``

/**
 * Erzeugt eine Liste an Draggable-Elementen.
 *
 * Im Gegensatz zur normalen Liste, kann diese Liste damit umgehen,
 * mit "position: absolute; bottom: **px" positioniert zu sein
 *
 * Drag-and-Drop-Events werden automatisch verwaltet, sofern updateItems synchron arbeitet.
 */
const Droppable: React.FC<DroppableProps> = ({
  items,
  children,
  updateItems,
  acceptsItem,
  disabled,
  type,
  highlightInsetLeft,
  highlightInsetRight,
  inverted = false,
  ...props
}) => {
  const id = useMemo(() => 'inverted-list-' + createId(), [])
  const placeholder = useRef<HTMLDivElement>(null)
  const [isDropDisabled, setIsDropDisabled] = useState(false)

  useDragDropHandler(
    useMemo(
      () => ({
        // Da react-brautiful-dnd zwischen onBeforeCapture und onBeforeDragStart die Top-Position der Liste
        // misst, müssen wir, wenn das Element aus einer anderen Liste kommt, unsere Top-Position so manipulieren,
        // als wäre das Element bereits in unserer Liste. Ansonsten kommt es zu hässlichen Glitches,
        // da react-brautiful-dnd nicht davon ausgeht, dass sich die Top-Position ändern kann.
        onBeforeCapture({ draggableId }) {
          const draggable = document.querySelector<HTMLElement>(
            '[data-rbd-draggable-id=' + JSON.stringify(draggableId) + ']'
          )
          const droppable = draggable ? draggable.closest<HTMLElement>('[data-rbd-droppable-id]') : null
          const droppableSourceId = droppable?.dataset.rbdDroppableId ?? ''
          const accept =
            typeof acceptsItem === 'function'
              ? acceptsItem({
                  draggableId,
                  item: draggableId.split('#')[0],
                  droppableSourceId,
                  droppableTargetId: id
                })
              : !disabled
          if (accept && droppableSourceId !== id && placeholder.current && draggable) {
            if (inverted) {
              placeholder.current.style.height = draggable.getBoundingClientRect().height + 'px'
            }
          }
          setIsDropDisabled(() => !accept)
        },
        onBeforeDragStart() {
          if (placeholder.current) {
            placeholder.current.style.height = '0px'
          }
        },
        onDragEnd(result) {
          setIsDropDisabled(false)
          if (result.destination?.droppableId !== id && result.source.droppableId !== id) {
            return
          }
          if (disabled || isDropDisabled) {
            return
          }
          const sourceIndex = result.source.droppableId === id ? result.source.index : undefined
          const destinationIndex = result.destination?.droppableId === id ? result.destination.index : undefined
          const item = result.draggableId.split('#')[0]
          updateItems(item, sourceIndex, destinationIndex)
        }
      }),
      [acceptsItem, disabled, id, isDropDisabled, updateItems, inverted]
    )
  )

  // Manipuliere den Placeholder, damit er sofort animiert, wenn das Element die Liste verlässt
  const placeholderExtraProps = useMemo(() => {
    let hasLeftLast = false
    return (snapshot: DroppableStateSnapshot, props: { on?: unknown; shouldAnimate?: boolean }) => {
      let result = { ...props }
      if (!snapshot.isDraggingOver && props.on) {
        result = {
          ...props,
          on: emptyOn,
          shouldAnimate: !hasLeftLast
        }
      } else if (snapshot.isDraggingOver && hasLeftLast) {
        result = {
          ...props,
          shouldAnimate: true
        }
      }

      hasLeftLast = !snapshot.isDraggingOver && !!snapshot.draggingFromThisWith
      return result
    }
  }, [])

  return (
    <DroppableOriginal
      droppableId={id}
      direction="vertical"
      type={type}
      isDropDisabled={disabled || isDropDisabled}
      renderClone={(provided, snapshot, rubric) => {
        const item = rubric.draggableId.split('#')[0]
        return (
          <ItemWrap
            {...provided.draggableProps}
            ref={provided.innerRef}
            style={patchDraggableStyle(provided.draggableProps.style, snapshot, {
              fadeWithoutHover: true
            })}
          >
            {children(item, items.indexOf(item), items, provided, snapshot, rubric)}
          </ItemWrap>
        )
      }}
    >
      {(droppableProvided, droppableSnapshot) => (
        <DroppableWrap
          type={type}
          insetLeft={highlightInsetLeft}
          insetRight={highlightInsetRight}
          enabled={!disabled && !isDropDisabled}
          {...props}
          ref={droppableProvided.innerRef}
          {...droppableProvided.droppableProps}
        >
          {items?.map((item, i) => (
            <Draggable isDragDisabled={disabled} key={i} index={i} draggableId={item + '#' + id + '-' + i}>
              {(provided, snapshot, rubric) => (
                <ItemWrap
                  key={i}
                  {...provided.draggableProps}
                  ref={provided.innerRef}
                  // Da der Placeholder bereits klein sein sollte, sieht es komisch aus,
                  // wenn die Elemente noch einmal animieren.
                  blockTransform={!droppableSnapshot.isDraggingOver}
                >
                  {children(item, i, items, provided, snapshot, rubric)}
                </ItemWrap>
              )}
            </Draggable>
          ))}
          {droppableProvided.placeholder
            ? React.cloneElement(
                droppableProvided.placeholder,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                placeholderExtraProps(droppableSnapshot, droppableProvided.placeholder.props)
              )
            : droppableProvided.placeholder}
          <div ref={placeholder} />
        </DroppableWrap>
      )}
    </DroppableOriginal>
  )
}

export default Droppable
